import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * useDuplicatesDetection
 *
 * Encapsula todo el flujo de **detección y limpieza de canciones duplicadas**
 * de la biblioteca:
 *
 *   - `loadDuplicates`         : detección "segura" con timeout de 30 s (auto en vista)
 *   - `detectDuplicates`       : detección completa (5 min timeout) desde botón
 *   - `detectExactDuplicates`  : match exacto título+artista
 *   - `handleDeleteDuplicate`  : borra una canción concreta y refresca el grupo
 *   - `handleRemoveDuplicates` : borra manteniendo la best_song de cada grupo (endpoint bulk)
 *   - `handleDeleteAllDuplicates`: borra en lotes de 20 respetando el keep-map
 *
 * Efecto auto: al activar `showDuplicatesView` y no estar cargado, dispara `loadDuplicates`.
 *
 * Extraído de `App.js` (feb-2026).
 *
 * @param {Object}   params
 * @param {string}   params.API
 * @param {boolean}  params.showDuplicatesView — flag de vista (queda en App.js)
 * @param {Function} params.fetchSongs         — refresca la biblioteca principal tras borrar
 */
export function useDuplicatesDetection({ API, showDuplicatesView, fetchSongs }) {
  const [isDuplicatesDialogOpen, setIsDuplicatesDialogOpen] = useState(false);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [isDetectingDuplicates, setIsDetectingDuplicates] = useState(false);
  const [duplicatesLoaded, setDuplicatesLoaded] = useState(false);
  const [duplicatesError, setDuplicatesError] = useState(false);
  const [selectedToKeepMap, setSelectedToKeepMap] = useState({});

  // Detección "segura" con timeout de 30 s
  const loadDuplicates = async () => {
    if (duplicatesLoaded) return;
    setIsDetectingDuplicates(true);
    setDuplicatesError(false);
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 30000)
      );
      const requestPromise = axios.get(`${API}/songs/detect-duplicates`);
      const response = await Promise.race([requestPromise, timeoutPromise]);
      setDuplicateGroups(response.data.duplicate_groups || []);
      setDuplicatesLoaded(true);
      setDuplicatesError(false);

      if (response.data.total_duplicates === 0) {
        toast.info('🎉 No hay duplicados en tu biblioteca');
      } else {
        toast.info(
          `✅ Encontrados ${response.data.total_duplicates} duplicados en ${response.data.duplicate_groups.length} grupos`
        );
      }
    } catch (error) {
      console.error('Error detecting duplicates:', error);
      setDuplicatesLoaded(false);
      setDuplicatesError(true);
      if (error.message === 'Timeout') {
        toast.error('⏱️ La búsqueda tardó demasiado. Por favor, inténtalo de nuevo.');
      } else {
        toast.error('❌ Error al buscar duplicados. Por favor, inténtalo de nuevo.');
      }
    } finally {
      setIsDetectingDuplicates(false);
    }
  };

  // Auto-load cuando el usuario abre la vista de duplicados
  useEffect(() => {
    if (showDuplicatesView && !duplicatesLoaded) {
      loadDuplicates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDuplicatesView]);

  // Detección completa (timeout 5 min) — usada desde botones manuales
  const detectDuplicates = async () => {
    try {
      setIsDetectingDuplicates(true);
      toast.info(
        '🔍 Buscando canciones duplicadas... Esto puede tardar varios minutos con bibliotecas grandes.'
      );
      const response = await axios.get(`${API}/songs/detect-duplicates`, {
        timeout: 300000,
      });
      if (response.data.duplicate_groups && response.data.duplicate_groups.length > 0) {
        setDuplicateGroups(response.data.duplicate_groups);
        const initialMap = {};
        response.data.duplicate_groups.forEach((group, index) => {
          initialMap[index] = group.best_song_id;
        });
        setSelectedToKeepMap(initialMap);
        setIsDuplicatesDialogOpen(true);
        setDuplicatesLoaded(true);
        toast.success(
          `Se encontraron ${response.data.duplicate_groups.length} grupos con ${response.data.total_duplicates} duplicados`
        );
      } else {
        toast.info('No se encontraron canciones duplicadas');
        setDuplicateGroups([]);
        setSelectedToKeepMap({});
      }
    } catch (error) {
      console.error('Error detecting duplicates:', error);
      setDuplicatesError(true);
      toast.error('Error al detectar duplicados');
    } finally {
      setIsDetectingDuplicates(false);
    }
  };

  // Detección exacta (título + artista idénticos)
  const detectExactDuplicates = async () => {
    try {
      setIsDetectingDuplicates(true);
      toast.info('🔍 Buscando duplicados EXACTOS (título + artista idénticos)...');
      const response = await axios.get(`${API}/songs/detect-exact-duplicates`, {
        timeout: 300000,
      });
      if (response.data.duplicate_groups && response.data.duplicate_groups.length > 0) {
        setDuplicateGroups(response.data.duplicate_groups);
        const initialMap = {};
        response.data.duplicate_groups.forEach((group, index) => {
          initialMap[index] = group.best_song_id;
        });
        setSelectedToKeepMap(initialMap);
        setIsDuplicatesDialogOpen(true);
        setDuplicatesLoaded(true);
        toast.success(
          `✅ ${response.data.duplicate_groups.length} grupos de duplicados exactos encontrados`
        );
      } else {
        toast.info('✅ No se encontraron duplicados exactos');
        setDuplicateGroups([]);
        setSelectedToKeepMap({});
      }
    } catch (error) {
      console.error('Error detecting exact duplicates:', error);
      toast.error('Error al detectar duplicados exactos');
    } finally {
      setIsDetectingDuplicates(false);
    }
  };

  // Borrar una canción concreta y actualizar los grupos localmente
  const handleDeleteDuplicate = async (songId, groupIndex) => {
    if (!window.confirm('¿Eliminar esta canción duplicada?')) return;
    try {
      await axios.delete(`${API}/songs/${songId}`);
      toast.success('Canción eliminada');

      const updatedGroups = [...duplicateGroups];
      updatedGroups[groupIndex].songs = updatedGroups[groupIndex].songs.filter(
        (s) => s.song.id !== songId
      );
      updatedGroups[groupIndex].count = updatedGroups[groupIndex].songs.length;

      if (updatedGroups[groupIndex].count <= 1) {
        updatedGroups.splice(groupIndex, 1);
      }
      setDuplicateGroups(updatedGroups);
      fetchSongs?.();
    } catch (error) {
      console.error('Error deleting duplicate:', error);
      toast.error('Error al eliminar');
    }
  };

  // Borrado bulk manteniendo la best_song_id de cada grupo (endpoint dedicado)
  const handleRemoveDuplicates = async (groups) => {
    try {
      const groupsToProcess = groups.map((g) => ({
        best_song_id: g.best_song_id,
        song_ids: g.songs.map((s) => s.song.id),
      }));
      const response = await axios.post(`${API}/songs/remove-duplicates`, {
        groups: groupsToProcess,
      });
      toast.success(`✅ ${response.data.deleted_count} canciones duplicadas eliminadas`);
      setIsDuplicatesDialogOpen(false);
      fetchSongs?.();
    } catch (error) {
      console.error('Error removing duplicates:', error);
      const errorMsg =
        error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al eliminar duplicados: ${errorMsg}`);
    }
  };

  // Borrado agresivo respetando keep-map del usuario, en lotes de 20
  const handleDeleteAllDuplicates = async () => {
    const totalToDelete = duplicateGroups.reduce((total, group, groupIndex) => {
      const selectedToKeep = selectedToKeepMap[groupIndex] || group.best_song_id;
      const toDeleteInGroup = group.songs.filter(
        (item) => item.song.id !== selectedToKeep
      ).length;
      return total + toDeleteInGroup;
    }, 0);

    if (totalToDelete === 0) {
      toast.info('No hay canciones duplicadas para eliminar');
      return;
    }
    if (
      !window.confirm(
        `¿Eliminar ${totalToDelete} canciones duplicadas de ${duplicateGroups.length} grupos?\n\nSe mantendrá la canción marcada (con más estrellas/información) en cada grupo.`
      )
    ) {
      return;
    }

    try {
      toast.info(`🗑️ Eliminando ${totalToDelete} duplicados...`);
      const songsToDelete = [];
      duplicateGroups.forEach((group, groupIndex) => {
        const selectedToKeep = selectedToKeepMap[groupIndex] || group.best_song_id;
        group.songs.forEach((item) => {
          if (item.song.id !== selectedToKeep) {
            songsToDelete.push(item.song.id);
          }
        });
      });

      // Lotes de 20 para evitar timeouts
      const batchSize = 20;
      let deletedCount = 0;
      for (let i = 0; i < songsToDelete.length; i += batchSize) {
        const batch = songsToDelete.slice(i, i + batchSize);
        await Promise.all(
          batch.map((id) =>
            axios.delete(`${API}/songs/${id}`).catch((err) => {
              console.error(`Error deleting song ${id}:`, err);
              return null;
            })
          )
        );
        deletedCount += batch.length;
        if (songsToDelete.length > batchSize) {
          toast.info(`Progreso: ${deletedCount}/${songsToDelete.length}`, {
            duration: 1000,
          });
        }
      }

      toast.success(`✅ ${deletedCount} canciones duplicadas eliminadas correctamente`);
      setDuplicateGroups([]);
      setSelectedToKeepMap({});
      setIsDuplicatesDialogOpen(false);
      setDuplicatesLoaded(false);
      fetchSongs?.();
    } catch (error) {
      console.error('Error deleting all duplicates:', error);
      toast.error('Error al eliminar duplicados. Algunos pueden no haberse eliminado.');
      fetchSongs?.();
    }
  };

  return {
    // state
    isDuplicatesDialogOpen,
    setIsDuplicatesDialogOpen,
    duplicateGroups,
    setDuplicateGroups,
    isDetectingDuplicates,
    duplicatesLoaded,
    duplicatesError,
    selectedToKeepMap,
    setSelectedToKeepMap,
    // handlers
    loadDuplicates,
    detectDuplicates,
    detectExactDuplicates,
    handleDeleteDuplicate,
    handleRemoveDuplicates,
    handleDeleteAllDuplicates,
  };
}
